import { useState, useEffect } from "react";
import { authService } from "@/services/authService";
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Hook centralizado para validación de permisos
 * Elimina lógica duplicada entre useMarginsPermissions, useOdooMigrationPermissions, etc.
 *
 * @param permissionChecks - Array de objetos con ruta, menu, funcion a validar
 * @returns Objeto con permisos validados y estado de carga
 *
 * @example
 * const permissions = usePermissions([
 *   { key: 'canView', ruta: '/analytics', menu: 'dashboard', funcion: 'ver' },
 *   { key: 'canExport', ruta: '/analytics', menu: 'dashboard', funcion: 'exportar' }
 * ]);
 *
 * if (permissions.canView) { ... }
 */

interface PermissionCheck {
  key: string;
  ruta: string;
  menu: string;
  funcion: string;
}

interface PermissionsResult {
  [key: string]: boolean;
  isLoading: boolean;
}

export function usePermissions(
  permissionChecks: PermissionCheck[],
): PermissionsResult {
  const [permissions, setPermissions] = useState<PermissionsResult>({
    isLoading: true,
  });

  // Los callers (useOdooMigrationPermissions, useMarginsPermissions, etc.)
  // pasan el array como literal hardcoded — cada render genera referencia
  // nueva. Sin esta estabilización por contenido, useEffect re-dispara en
  // cada render → loop infinito de POST /api/auth/sync-user.
  const checksKey = JSON.stringify(permissionChecks);

  useEffect(() => {
    async function checkPermissions() {
      try {
        // Verificar que el usuario esté autenticado
        const session = await fetchAuthSession();

        if (!session.tokens?.idToken) {
          // Usuario no autenticado, denegar todos los permisos
          const deniedPermissions: PermissionsResult = { isLoading: false };
          permissionChecks.forEach((check) => {
            deniedPermissions[check.key] = false;
          });
          setPermissions(deniedPermissions);
          return;
        }

        // Obtener permisos del usuario desde backend
        const userSession = await authService.syncUser();

        // Verificar si tiene permiso global "all"
        const hasAllPermissions = userSession?.data?.permissions?.some(
          (permission: any) =>
            permission.estructura_permisos?.["all"]?.["all"]?.["all"] ===
              true || permission.nombre === "all",
        );

        if (hasAllPermissions) {
          // Usuario con acceso total, conceder todos los permisos
          const allPermissions: PermissionsResult = { isLoading: false };
          permissionChecks.forEach((check) => {
            allPermissions[check.key] = true;
          });
          setPermissions(allPermissions);
          return;
        }

        // Validar permisos individuales en paralelo
        const validationPromises = permissionChecks.map(async (check) => {
          try {
            const hasPermission = await authService.validatePermission(
              check.ruta,
              check.menu,
              check.funcion,
            );
            return { key: check.key, value: hasPermission };
          } catch (error) {
            console.error(`Error validando permiso ${check.key}:`, error);
            return { key: check.key, value: false };
          }
        });

        const results = await Promise.all(validationPromises);

        // Construir objeto de resultados
        const validatedPermissions: PermissionsResult = { isLoading: false };
        results.forEach((result) => {
          validatedPermissions[result.key] = result.value;
        });

        setPermissions(validatedPermissions);
      } catch (error) {
        console.error("Error verificando permisos:", error);

        // En caso de error, denegar todos los permisos por seguridad
        const deniedPermissions: PermissionsResult = { isLoading: false };
        permissionChecks.forEach((check) => {
          deniedPermissions[check.key] = false;
        });
        setPermissions(deniedPermissions);
      }
    }

    checkPermissions();
  }, [checksKey]);

  return permissions;
}

/**
 * Hook especializado para permisos de Margins/Analytics
 * Migrado a usar el hook centralizado
 */
export function useMarginsPermissions() {
  return usePermissions([
    {
      key: "canViewDashboard",
      ruta: "/analytics",
      menu: "dashboard",
      funcion: "ver",
    },
    {
      key: "canExportDashboard",
      ruta: "/analytics",
      menu: "dashboard",
      funcion: "exportar",
    },
    {
      key: "canViewDetails",
      ruta: "/analytics",
      menu: "dashboard",
      funcion: "ver-detalles",
    },
    {
      key: "canViewAnalytics",
      ruta: "/analytics",
      menu: "trends",
      funcion: "ver",
    },
    {
      key: "canViewProducts",
      ruta: "/analytics",
      menu: "products",
      funcion: "ver",
    },
    {
      key: "canViewStores",
      ruta: "/analytics",
      menu: "stores",
      funcion: "ver",
    },
    {
      key: "canExportData",
      ruta: "/analytics",
      menu: "data",
      funcion: "exportar",
    },
    {
      key: "canCompareData",
      ruta: "/analytics",
      menu: "compare",
      funcion: "comparar",
    },
  ]);
}

/**
 * Hook especializado para permisos de Odoo Migration
 * Migrado a usar el hook centralizado
 */
export function useOdooMigrationPermissions() {
  return usePermissions([
    {
      key: "canView",
      ruta: "/odoo-erp",
      menu: "migration-produccion",
      funcion: "ver",
    },
    {
      key: "canExecuteManual",
      ruta: "/odoo-erp",
      menu: "migration-produccion",
      funcion: "ejecutar-manual",
    },
    {
      key: "canViewLogs",
      ruta: "/odoo-erp",
      menu: "migration-produccion",
      funcion: "ver-logs",
    },
    {
      key: "canConfigure",
      ruta: "/odoo-erp",
      menu: "migration-produccion",
      funcion: "configurar",
    },
    {
      key: "canCancel",
      ruta: "/odoo-erp",
      menu: "migration-produccion",
      funcion: "cancelar",
    },
    {
      key: "canViewHistory",
      ruta: "/odoo-erp",
      menu: "migration-produccion",
      funcion: "historico",
    },
  ]);
}

// Hook useControlDiarioPermissions eliminado 2026-05-20:
// el módulo employee-schedules (puestos/horarios/control diario) fue rollbackeado
// en commit 34ad166da (2026-04-22) junto con distribución v0.3.0. Funcionalidad
// declarada fuera de scope durante migración v1.x→v2.0 Opción 2.
